import React, { useRef, useMemo } from "react";
import { CartItem, Currency } from "@pearson-ell/commerce-sdk";
import {
    StyledCartProduct,
    StyledInnerContainer,
    StyledImage,
    StyledProductInfo,
    StyledProductNameContainer,
    StyledProductName,
    StyledProductPriceContainer,
    StyledProductPrice,
    StyledButton,
    StyledLeftFlexBlock,
    StyledRightFlexBlock,
    StyledDisabledProductPrice,
    StyledProductTitle,
    StyledProductDescription,
    StyledDiscountWrapper,
    EnumStyledCartProductBreakPoints,
} from "./CartProduct.parts";
import noImageSrc from "../../assets/images/no-image.png";
import { cutText, formatPrice } from "../../commons/utils";
import { MAX_PRODUCT_NAME_DISPLAY_LENGTH } from "../../commons/constants";
import { BucketSvg } from "../../commons/svgs";
import parse from "html-react-parser";
import { useTranslation } from "react-i18next";
import { useBreakpoints } from "../../commons/hooks";
import { ItemQuantity } from "../ItemQuantity/ItemQuantity";

export type Props = {
    item: CartItem;
    currency: Currency;
    onChange: (
        ev: React.ChangeEvent<HTMLInputElement>,
        item?: CartItem
    ) => void | Promise<void>;
    onDelete: (
        e:
            | React.MouseEvent<HTMLButtonElement>
            | React.KeyboardEvent<HTMLButtonElement>,
        itemId: string
    ) => void;
    hasDescription?: boolean;
    debounceChangeQty?: number;
    isBusy?: boolean;
    isHighlighted?: boolean;
};

export const CartProduct: React.FC<Props> = ({
    item,
    currency,
    onChange,
    onDelete,
    debounceChangeQty,
    isBusy,
    hasDescription = false,
    isHighlighted = false,
}) => {
    const parsedShortDescription = useMemo(
        () => parse(item.shortDescription ?? ""),
        [item.shortDescription]
    );
    const containerRef = useRef<HTMLDivElement | null>(null);
    const { t } = useTranslation();

    const onChangeQuantity = (
        ev: React.ChangeEvent<HTMLInputElement>,
        item?: CartItem
    ) => {
        if (item) {
            const quantity = Number.parseInt(ev.target.value);
            if (
                !Number.isNaN(quantity) &&
                quantity >= item.minPurchaseQuantity &&
                quantity <= item.maxPurchaseQuantity
            ) {
                onChange(ev, item);
            }
        }
    };

    const breakpoint = useBreakpoints<EnumStyledCartProductBreakPoints>(
        containerRef,
        [
            EnumStyledCartProductBreakPoints.mobileSm,
            EnumStyledCartProductBreakPoints.mobileMd,
            EnumStyledCartProductBreakPoints.tabletSm,
            EnumStyledCartProductBreakPoints.tabletMd,
            EnumStyledCartProductBreakPoints.tabletLg,
            EnumStyledCartProductBreakPoints.desktopSm,
        ]
    );

    return (
        <StyledCartProduct
            key={item.id}
            ref={containerRef}
            breakpoint={breakpoint}
            className={isHighlighted ? "highlighted" : ""}
        >
            <StyledInnerContainer className="innerContainer">
                <StyledLeftFlexBlock className={"leftFlexBlock"}>
                    <StyledImage
                        role="presentation"
                        className={"image"}
                        src={item.imageUrl || noImageSrc}
                        alt={item.name}
                    />
                </StyledLeftFlexBlock>

                <StyledRightFlexBlock>
                    <StyledProductInfo className={"productInfo"}>
                        <StyledProductNameContainer
                            className={"productNameContainer"}
                        >
                            <StyledProductName className={"productName"}>
                                <StyledProductTitle>
                                    {cutText(
                                        item.name,
                                        MAX_PRODUCT_NAME_DISPLAY_LENGTH
                                    )}
                                </StyledProductTitle>
                                {hasDescription && item.shortDescription && (
                                    <StyledProductDescription
                                        className={"productDescription"}
                                    >
                                        {parsedShortDescription}
                                    </StyledProductDescription>
                                )}
                            </StyledProductName>
                            <ItemQuantity
                                item={item}
                                onChange={onChangeQuantity}
                                debounceChangeQty={debounceChangeQty}
                                isBusy={isBusy}
                            ></ItemQuantity>{" "}
                        </StyledProductNameContainer>
                        <StyledProductPriceContainer
                            className={"productPriceContainer"}
                        >
                            {item.totalOriginalPrice ===
                                item.totalSalePrice && (
                                <StyledProductPrice className={"productPrice"}>
                                    {formatPrice(
                                        item.totalOriginalPrice,
                                        currency
                                    )}
                                </StyledProductPrice>
                            )}
                            {item.totalOriginalPrice !== item.totalSalePrice &&
                                item.totalSalePrice > 0 && (
                                    <StyledDiscountWrapper
                                        className={"discountWrapper"}
                                    >
                                        <StyledProductPrice
                                            className={
                                                "discount-price-container productPrice"
                                            }
                                        >
                                            {formatPrice(
                                                item.totalSalePrice,
                                                currency
                                            )}
                                        </StyledProductPrice>
                                        <StyledDisabledProductPrice
                                            className={
                                                "discount-price-container disabledProductPrice"
                                            }
                                        >
                                            {formatPrice(
                                                item.totalOriginalPrice,
                                                currency
                                            )}
                                        </StyledDisabledProductPrice>
                                    </StyledDiscountWrapper>
                                )}
                            {item.totalOriginalPrice !== 0 &&
                                item.totalSalePrice === 0 && (
                                    <StyledProductPrice
                                        className={"productPrice"}
                                    >
                                        {formatPrice(
                                            item.totalOriginalPrice,
                                            currency
                                        )}
                                    </StyledProductPrice>
                                )}
                            <StyledButton
                                className={"button"}
                                aria-label={`${t("remove")} ${item.name}`}
                                onClick={(
                                    ev:
                                        | React.MouseEvent<HTMLButtonElement>
                                        | React.KeyboardEvent<HTMLButtonElement>
                                ) => onDelete(ev, item.id)}
                            >
                                <i>
                                    <BucketSvg aria-hidden={true} />
                                </i>
                                {t("remove")}
                            </StyledButton>
                        </StyledProductPriceContainer>
                    </StyledProductInfo>
                </StyledRightFlexBlock>
            </StyledInnerContainer>
        </StyledCartProduct>
    );
};
